import React, { useEffect, useState, useRef } from "react";
import CoordinatorHandler from "./components/CorporatecoordinatorHandler";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import CoordinatorHeader from "./components/CorporatecoordinatorHeader";
import CoordinatorFilters from "./components/CorporatecoordinatorFilters";
import CoordinatorGridTable from "./components/CorporatecoordinatorGridTable";

const Coordinator = () => {
  const [currentCoordinator, setCurrentCoordinator] = useState(null);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [filterHeight, setFilterHeight] = useState(0);
  const [radiologists, setRadiologists] = useState([]);
  const [selectedRadiologist, setSelectedRadiologist] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [isUploadingNote, setIsUploadingNote] = useState(false);
  const [bodyParts, setBodyParts] = useState([]);
  const [assignMode, setAssignMode] = useState("assign");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const navigate = useNavigate();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const filterRef = useRef(null);
  const headerRef = useRef(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [bulkNoteText, setBulkNoteText] = useState("");
  const [isUpdatingBulkNotes, setIsUpdatingBulkNotes] = useState(false);
  const [corporateCoordinators, setCorporateCoordinators] = useState([]);
  const [selectedCorporate, setSelectedCorporate] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  const [filters, setFilters] = useState({
    bodyPart: "",
    allocated: "",
    clinicalHistory: "",
    status: "",
    modality: "",
    studyDate: "",
    radiologist: "",
  });

  const [liveTimeRemaining, setLiveTimeRemaining] = useState({});
  const [stats, setStats] = useState({
    totalCases: 0,
    pendingCases: 0,
    reportedCases: 0,
    overdueCases: 0,
  });

  const user = JSON.parse(localStorage.getItem("user"))?.user;
  const isCorporate = user?.group === "corporatecoordinator";
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";

  const handleSendToU4rad = async () => {
    if (selectedPatients.length === 0) {
      alert("Select at least one case");
      return;
    }
  
    try {
      const res = await CoordinatorHandler.sendToU4rad(selectedPatients);
  
      if (res.success) {
        // alert(`Sent ${res.updated_count} cases to U4RAD`);
        await fetchData();
        setSelectedPatients([]);
      }
    } catch (error) {
      console.error("Error sending to U4RAD:", error);
      alert("Failed to send cases");
    }
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(savedDarkMode);
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode.toString());
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
    if (filterRef.current) {
      setFilterHeight(filterRef.current.offsetHeight);
    }
  }, [loading]);

  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
      if (filterRef.current) {
        setFilterHeight(filterRef.current.offsetHeight);
      }
    };

    updateHeights();

    // Add resize observer for dynamic height changes
    const resizeObserver = new ResizeObserver(updateHeights);

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    if (filterRef.current) {
      resizeObserver.observe(filterRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 1000); // 500ms delay
    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const getUniqueOptions = (field) => {
    const options = patients
      .map((p) => {
        switch (field) {
          case "bodyPart":
            return p.body_part_examined;
          case "allocated":
            return p.radiologist?.length > 0 ? "Assigned" : "Unassigned";
          case "status":
            return p.is_done ? "Completed" : "Pending";
          case "modality":
            return p.modality;
          default:
            return "";
        }
      })
      .filter((option) => option && option !== "Unknown");
    return [...new Set(options)];
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        coordinatorData,
        patientData,
        radiologistData,
        bodyPartsData,
        caseCounts,
        corporateData,
      ] = await Promise.all([
        CoordinatorHandler.getCoordinators(),
        CoordinatorHandler.getTatCounters(),
        CoordinatorHandler.getRadiologists().catch(() => []),
        CoordinatorHandler.getBodyParts().catch(() => []),
        CoordinatorHandler.getCaseCounts(),
        CoordinatorHandler.getCorporateCoordinators().catch(() => [])
      ]);

      // Process patients to add awaitingHistory flag
      const now = new Date();
      const processedPatients = (patientData.results || []).map((patient) => {
        // Check if notes_modified_at is null (awaiting history)
        if (!patient.notes_modified_at) {
          const receivedOnDb = new Date(patient.recived_on_db);
          const elapsedSeconds = Math.floor((now - receivedOnDb) / 1000);

          return {
            ...patient,
            awaitingHistory: true,
            elapsed_seconds: elapsedSeconds,
            time_remaining: null,
            overdue_seconds: 0,
          };
        }

        // Normal TAT logic
        return {
          ...patient,
          awaitingHistory: false,
        };
      });

      // Calculate overdue cases from processedPatients
      const overdueCases =
        processedPatients.filter(
          (patient) =>
            patient.tat_breached === true &&
            (!patient.patient_reports || patient.patient_reports.length === 0)
        ).length || 0;

      setCorporateCoordinators(corporateData);
      setCurrentCoordinator(coordinatorData);
      setPatients(processedPatients);
      setFilteredPatients(processedPatients);
      setRadiologists(radiologistData);
      setBodyParts(bodyPartsData);

      if (caseCounts) {
        setStats({
          totalCases: caseCounts.total_cases || 0,
          pendingCases: caseCounts.pending_cases || 0,
          reportedCases: caseCounts.reported_cases || 0,
          overdueCases: overdueCases,
        });
      }

      setNextCursor(patientData.next_cursor);
      setHasMore(!!patientData.next_cursor);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMorePatients = async () => {
    if (!nextCursor || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const patientData = await CoordinatorHandler.getTatCounters(nextCursor);
      if (patientData.results) {
        // Process new patients
        const now = new Date();
        const processedNewPatients = patientData.results.map((patient) => {
          if (!patient.notes_modified_at) {
            const receivedOnDb = new Date(patient.recived_on_db);
            const elapsedSeconds = Math.floor((now - receivedOnDb) / 1000);

            return {
              ...patient,
              awaitingHistory: true,
              elapsed_seconds: elapsedSeconds,
              time_remaining: null,
              overdue_seconds: 0,
            };
          }

          return {
            ...patient,
            awaitingHistory: false,
          };
        });

        const existingIds = new Set(patients.map((p) => p.id));
        const newPatients = processedNewPatients.filter(
          (p) => !existingIds.has(p.id)
        );
        setPatients((prev) => [...prev, ...newPatients]);
        setNextCursor(patientData.next_cursor);
        setHasMore(!!patientData.next_cursor);
      }
    } catch (error) {
      console.error("Error loading more patients:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (patients.length > 0) {
      const initialTimes = {};
      patients.forEach((patient) => {
        if (patient.awaitingHistory) {
          // For awaiting history, use elapsed_seconds
          initialTimes[patient.id] = patient.elapsed_seconds || 0;
        } else if (!patient.is_done && patient.time_remaining !== null) {
          // For normal TAT countdown
          initialTimes[patient.id] = patient.time_remaining;
        }
      });
      setLiveTimeRemaining(initialTimes);
    }
  }, [patients]);

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveTimeRemaining((prev) => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach((patientId) => {
          const patient = patients.find((p) => p.id === parseInt(patientId));

          if (patient && !patient.is_done) {
            if (patient.awaitingHistory) {
              // Count UP for awaiting history
              updated[patientId] = updated[patientId] + 1;
              hasChanges = true;
            } else {
              // Count DOWN for normal TAT
              updated[patientId] = updated[patientId] - 1;
              hasChanges = true;
            }
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [patients]);

  useEffect(() => {
    const applyFiltersAndSort = (patientsToFilter) => {
      let filtered = [...patientsToFilter];

      if (filters.bodyPart) {
        filtered = filtered.filter(
          (p) => p.body_part_examined === filters.bodyPart
        );
      }
      if (filters.allocated) {
        const isAssigned = filters.allocated === "Assigned";
        filtered = filtered.filter((p) =>
          isAssigned ? p.radiologist?.length > 0 : p.radiologist?.length === 0
        );
      }
      if (filters.status) {
        const isDone = filters.status === "Completed";
        filtered = filtered.filter((p) => p.is_done === isDone);
      }
      if (filters.modality) {
        filtered = filtered.filter((p) => p.modality === filters.modality);
      }
      if (filters.studyDate) {
        filtered = filtered.filter((p) => p.study_date === filters.studyDate);
      }
      if (filters.radiologist) {
        filtered = filtered.filter(
          (p) =>
            p.radiologist &&
            p.radiologist.some(
              (rad) =>
                rad ===
                radiologists.find((r) => r.id === parseInt(filters.radiologist))
                  ?.first_name +
                  " " +
                  radiologists.find(
                    (r) => r.id === parseInt(filters.radiologist)
                  )?.last_name
            )
        );
      }

      return filtered;
    };

    const handleSearch = async () => {
      if (debouncedSearchTerm) {
        setSearchLoading(true);
        try {
          const searchResults = await CoordinatorHandler.searchPatients(
            debouncedSearchTerm
          );
          const sortedResults = sortPatients(searchResults);
          setFilteredPatients(applyFiltersAndSort(sortedResults));
        } catch (error) {
          console.error("Search failed:", error);
          setFilteredPatients([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        const sortedPatients = sortPatients(patients);
        setFilteredPatients(applyFiltersAndSort(sortedPatients));
      }
    };

    const sortPatients = (patientsToSort) => {
      return patientsToSort;  // no sorting applied
    };

    handleSearch();
  }, [filters, patients, debouncedSearchTerm]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handlePatientSelect = (patientId) => {
    if (patientId === "all") {
      setSelectedPatients(filteredPatients.map((p) => p.id));
    } else if (patientId === "clear") {
      setSelectedPatients([]);
    } else {
      setSelectedPatients((prev) =>
        prev.includes(patientId)
          ? prev.filter((id) => id !== patientId)
          : [...prev, patientId]
      );
    }
  };

  // const handleAssignRadiologist = async () => {
  //   if (!selectedRadiologist || selectedPatients.length === 0) {
  //     alert("Please select a radiologist and at least one patient.");
  //     return;
  //   }

  //   try {
  //     const apiCall =
  //       assignMode === "assign"
  //         ? CoordinatorHandler.assignRadiologist
  //         : CoordinatorHandler.replaceRadiologist;

  //     // const validPatients = selectedPatients.filter((patientId) => {
  //     //   const patient = patients.find((p) => p.id === Number(patientId));
  //     //   return (
  //     //     patient &&
  //     //     patient.body_part_examined &&
  //     //     patient.body_part_examined.trim() !== ""
  //     //   );
  //     // });

  //     const validPatients = selectedPatients.filter((patientId) => {
  //       const pid = Number(patientId);
      
  //       // const patient = patients.find((p) => Number(p.id) == pid);
  //       const patient = patients?.find((p) => Number(p.id) === pid) || null;

      
  //       console.log("Checking patient:", pid, "found:", patient);
      
  //       return (
  //         patient &&
  //         patient.body_part_examined &&
  //         patient.body_part_examined.trim() !== ""
  //       );
  //     });


  //     if (validPatients.length === 0) {
  //       alert("No valid patients found for assignment (invalid body part).");
  //       return;
  //     }

  //     await Promise.all(
  //       selectedPatients.map((patientId) =>
  //         apiCall(patientId, selectedRadiologist)
  //       )
  //     );

  //     await fetchData();
  //     setSelectedPatients([]);
  //     setSelectedRadiologist("");
  //     alert(
  //       `Radiologist ${
  //         assignMode === "assign" ? "assigned" : "replaced"
  //       } successfully!`
  //     );
  //   } catch (error) {
  //     console.error(
  //       `Error ${
  //         assignMode === "assign" ? "assigning" : "replacing"
  //       } radiologist:`,
  //       error
  //     );
  //     alert(
  //       `Error ${
  //         assignMode === "assign" ? "assigning" : "replacing"
  //       } radiologist. Please try again.`
  //     );
  //   }
  // };

  
  // const handleAssignRadiologist = async () => {
  //   console.log("========== ASSIGN FUNCTION TRIGGERED ==========");
  
  //   console.log("Selected Radiologist:", selectedRadiologist);
  //   console.log("Selected Patients:", selectedPatients);
  
  //   console.log("Full Patients State:", patients);
  //   console.log("Patients IDs:", patients.map(p => p.id));
  
  //   if (!selectedRadiologist || selectedPatients.length === 0) {
  //     alert("Please select a radiologist and at least one patient.");
  //     return;
  //   }
  
  //   try {
  //     const apiCall =
  //       assignMode === "assign"
  //         ? CoordinatorHandler.assignRadiologist
  //         : CoordinatorHandler.replaceRadiologist;
  
  //     console.log("API being used:", assignMode === "assign" ? "Assign" : "Replace");
  
  //     // DEBUG EACH PATIENT ID CHECK
  //     const validPatients = selectedPatients.filter((patientId) => {
  //       const pid = Number(patientId);
  
  //       console.log("----- Checking Patient ID:", pid, "-----");
  
  //       // Try find match from patients array
  //       const patient = patients.find((p) => Number(p.id) === pid);
  
  //       console.log("Matched Patient Object:", patient);
  //       console.log("Body Part:", patient ? patient.body_part_examined : "N/A");
  
  //       return (
  //         patient &&
  //         patient.body_part_examined &&
  //         patient.body_part_examined.trim() !== ""
  //       );
  //     });
  
  //     console.log("Valid Patients after filter:", validPatients);
  
  //     if (validPatients.length === 0) {
  //       console.log("STOPPED: No valid patients found.");
  //       alert("No valid patients found for assignment (invalid body part).");
  //       return;
  //     }
  
  //     console.log("Proceeding with API calls for:", validPatients);
  
  //     // CALL API ONE BY ONE — DEBUG PRINT
  //     await Promise.all(
  //       validPatients.map((patientId) => {
  //         console.log("Calling API for Patient:", patientId);
  //         return apiCall(patientId, selectedRadiologist);
  //       })
  //     );
  
  //     console.log("API calls completed. Fetching updated data...");
  
  //     await fetchData();
  
  //     console.log("Resetting selected radiologist and patient selection...");
  //     setSelectedPatients([]);
  //     setSelectedRadiologist("");
  
  //     alert(
  //       `Radiologist ${
  //         assignMode === "assign" ? "assigned" : "replaced"
  //       } successfully!`
  //     );
  
  //     console.log("========== ASSIGN COMPLETED SUCCESSFULLY ==========");
  
  //   } catch (error) {
  //     console.error(
  //       `Error ${
  //         assignMode === "assign" ? "assigning" : "replacing"
  //       } radiologist:`,
  //       error
  //     );
  
  //     alert(
  //       `Error ${
  //         assignMode === "assign" ? "assigning" : "replacing"
  //       } radiologist. Please try again.`
  //     );
  //   }
  // };


  const handleAssignRadiologist = async () => {
    console.log("========== ASSIGN FUNCTION TRIGGERED ==========");
    console.log("Selected Radiologist:", selectedRadiologist);
    console.log("Selected Patients:", selectedPatients);
  
    // Use filtered/full patients instead of the initial 400
    const currentPatientList = filteredPatients.length > 0 ? filteredPatients : patients;
  
    if (!selectedRadiologist || selectedPatients.length === 0) {
      alert("Please select a radiologist and at least one patient.");
      return;
    }
  
    try {
      const apiCall =
        assignMode === "assign"
          ? CoordinatorHandler.assignRadiologist
          : CoordinatorHandler.replaceRadiologist;
  
      console.log("API being used:", assignMode === "assign" ? "Assign" : "Replace");
  
      const validPatients = selectedPatients.filter((patientId) => {
        const pid = Number(patientId);
  
        console.log("----- Checking Patient ID:", pid, "-----");
  
        const patient = currentPatientList.find((p) => Number(p.id) === pid);
  
        console.log("Matched Patient Object:", patient);
        console.log("Body Part:", patient ? patient.body_part_examined : "N/A");
  
        return patient && patient.body_part_examined && patient.body_part_examined.trim() !== "";
      });
  
      console.log("Valid Patients after filter:", validPatients);
  
      if (validPatients.length === 0) {
        console.log("STOPPED: No valid patients found.");
        alert("No valid patients found for assignment (invalid body part).");
        return;
      }
  
      console.log("Proceeding with API calls for:", validPatients);
  
      await Promise.all(
        validPatients.map((patientId) => apiCall(patientId, selectedRadiologist))
      );
  
      console.log("API calls completed. Fetching updated data...");
      await fetchData();
  
      setSelectedPatients([]);
      setSelectedRadiologist("");
  
      // alert(
      //   `Radiologist ${assignMode === "assign" ? "assigned" : "replaced"} successfully!`
      // );
  
      console.log("========== ASSIGN COMPLETED SUCCESSFULLY ==========");
    } catch (error) {
      console.error(
        `Error ${assignMode === "assign" ? "assigning" : "replacing"} radiologist:`,
        error
      );
      alert(`Error ${assignMode === "assign" ? "assigning" : "replacing"} radiologist. Please try again.`);
    }
  };

  // const handleAssignCorporate = async () => {
  //   if (!selectedCorporate || selectedPatients.length === 0) {
  //     alert("Select corporate coordinator & cases");
  //     return;
  //   }
  //   try {
  //     await CoordinatorHandler.assignCorporateCoordinator(
  //       selectedPatients,
  //       selectedCorporate
  //     );
  //     await fetchData();
  //     setSelectedCorporate("");
  //     setSelectedPatients([]);
  //   } catch (error) {
  //     alert("Failed to assign corporate coordinator");
  //   }
  // };

  const handleAssignCorporate = async () => {
    if (!selectedCorporate || selectedPatients.length === 0) {
      alert("Select corporate coordinator & cases");
      return;
    }
    try {
      if (assignMode === "assign") {
        await CoordinatorHandler.assignCorporateCoordinator(
          selectedPatients,
          selectedCorporate
        );
      } else {
        await CoordinatorHandler.replaceCorporateCoordinator(
          selectedPatients,
          selectedCorporate
        );
      }
      await fetchData();
      setSelectedCorporate("");
      setSelectedPatients([]);
    } catch (error) {
      alert("Failed to update corporate coordinator");
    }
  };

  // Upload note for selected patients and refresh list (prefer date-range refresh if applied)
  const handleUploadNote = async () => {
    if (selectedPatients.length === 0) {
      alert("Please select at least one patient to upload a note.");
      return;
    }

    if (!noteText || !noteText.trim()) {
      alert("Please enter a note before sending.");
      return;
    }

    try {
      setIsUploadingNote(true);
      const response = await CoordinatorHandler.uploadPatientNote(
        selectedPatients,
        noteText
      );

      if (response && response.success) {
        // alert("Note uploaded successfully");
        setNoteText("");
      } else {
        const msg =
          (response && response.error) ||
          (response && response.data && response.data.message) ||
          "Failed to upload note";
        console.error("Upload note response:", response);
        alert(msg);
      }

      // Refresh patients. If a studyDate filter range is present ("YYYY-MM-DD to YYYY-MM-DD"), use it to call getPatientsByDate.
      if (filters.studyDate && filters.studyDate.includes(" to ")) {
        const [start, end] = filters.studyDate
          .split(" to ")
          .map((s) => s.trim());
        try {
          const data = await CoordinatorHandler.getPatientsByDate(start, end);
          setPatients(data.results || data || []);
          setFilteredPatients(data.results || data || []);
          setNextCursor(data.next_cursor || null);
          setHasMore(!!data.next_cursor);
        } catch (err) {
          console.error(
            "Failed to refresh patients by date after uploading note:",
            err
          );
          await fetchData();
        }
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Error uploading patient note:", error);
      alert("Failed to upload note. Please try again.");
    } finally {
      setIsUploadingNote(false);
    }
  };

  // Bulk update notes for selected patients
  const handleBulkUpdateNotes = async () => {
    if (selectedPatients.length === 0) {
      alert("Please select at least one patient to update notes.");
      return;
    }

    if (!bulkNoteText || !bulkNoteText.trim()) {
      alert("Please enter notes text before updating.");
      return;
    }

    try {
      setIsUpdatingBulkNotes(true);
      const response = await CoordinatorHandler.bulkUpdateNotes(
        selectedPatients,
        bulkNoteText
      );

      if (response && response.success) {
        // alert(
        //   `Successfully updated notes for ${response.updated_count} patients`
        // );
        setBulkNoteText("");

        // Refresh the data using the same logic as handleUploadNote
        if (filters.studyDate && filters.studyDate.includes(" to ")) {
          const [start, end] = filters.studyDate
            .split(" to ")
            .map((s) => s.trim());
          try {
            const data = await CoordinatorHandler.getPatientsByDate(start, end);
            setPatients(data.results || data || []);
            setFilteredPatients(data.results || data || []);
            setNextCursor(data.next_cursor || null);
            setHasMore(!!data.next_cursor);
          } catch (err) {
            console.error(
              "Failed to refresh patients by date after bulk notes update:",
              err
            );
            await fetchData();
          }
        } else {
          await fetchData();
        }
      } else {
        const errorMsg = response?.error || "Failed to update notes";
        alert(errorMsg);
      }
    } catch (error) {
      console.error("Error in bulk notes update:", error);
      alert("Failed to update notes. Please try again.");
    } finally {
      setIsUpdatingBulkNotes(false);
    }
  };

  const handleSaveReport = async (newData, historyFiles) => {
    const apiData = {
      patient_name: newData.patient_name,
      patient_id: newData.patient_id,
      age: newData.age,
      gender: newData.gender,
      study_description: newData.study_description,
      notes: newData.notes,
      modality: newData.modality,
      body_part_examined: newData.body_part_examined,
      referring_doctor_name: newData.referring_doctor_name,
      email: newData.email,
      whatsapp_number: newData.whatsapp_number,
      contrast_used: newData.contrast_used,
      is_follow_up: newData.is_follow_up,
      imaging_views: newData.imaging_views,
    };

    try {
      await CoordinatorHandler.updatePatient(selectedReport.id, apiData);
      if (historyFiles && historyFiles.length > 0) {
        await CoordinatorHandler.uploadHistoryFiles(
          selectedReport.id,
          historyFiles
        );
      } else {
        console.log("No history files to upload.");
      }

      // alert("Patient updated successfully ✅");
      setSelectedReport(null);
      fetchData();
    } catch (error) {
      console.error("Error updating patient:", error);
      alert("Failed to update patient ❌");
    }
  };

  const bodyPartOptions = bodyParts
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((part) => ({
      value: part.name,
      label: part.name,
    }));

  const formatTimeRemaining = (
    timeRemaining,
    isDone,
    overdueSeconds,
    awaitingHistory = false
  ) => {
    if (isDone) {
      if (overdueSeconds) {
        const absTime = Math.abs(overdueSeconds);
        const hours = Math.floor(absTime / 3600);
        const minutes = Math.floor((absTime % 3600) / 60);
        const seconds = absTime % 60;
        return `Completed in ${hours}h ${minutes}m ${seconds}s`;
      }
      return "Completed";
    }

    // Handle awaiting history - COUNT UP
    if (awaitingHistory) {
      if (timeRemaining === null || timeRemaining === undefined) {
        return "Awaiting History • 00:00:00";
      }
      const absTime = Math.abs(timeRemaining);
      const hours = Math.floor(absTime / 3600);
      const minutes = Math.floor((absTime % 3600) / 60);
      const seconds = absTime % 60;
      return `Awaiting History • ${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    // Normal TAT countdown
    if (timeRemaining === null || timeRemaining === undefined) return "N/A";

    const absTime = Math.abs(timeRemaining);
    const hours = Math.floor(absTime / 3600);
    const minutes = Math.floor((absTime % 3600) / 60);
    const seconds = absTime % 60;

    if (timeRemaining < 0) {
      return `Overdue by ${hours}h ${minutes}m ${seconds}s`;
    } else {
      return `${hours}h ${minutes}m ${seconds}s remaining`;
    }
  };

  const getLiveTimeRemaining = (patientId, fallbackTime) => {
    return liveTimeRemaining[patientId] !== undefined
      ? liveTimeRemaining[patientId]
      : fallbackTime;
  };

  const getTimeRemainingColor = (
    timeRemaining,
    tatBreached,
    isDone,
    awaitingHistory = false
  ) => {
    if (isDone) return darkMode ? "text-emerald-300" : "text-emerald-700";
    if (awaitingHistory) return darkMode ? "text-blue-400" : "text-blue-600"; // Blue for awaiting history
    if (tatBreached || timeRemaining < 0)
      return darkMode ? "text-red-300" : "text-red-700";
    if (timeRemaining < 3600)
      return darkMode ? "text-amber-300" : "text-amber-700";
    return darkMode ? "text-emerald-300" : "text-emerald-700";
  };

  const renderGridView = () => (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPatients.map((patient) => (
          <div
            key={patient.id}
            className={`rounded-xl shadow-md border transition-all duration-200 hover:shadow-lg cursor-pointer ${
              darkMode
                ? "bg-gray-800 border-gray-700 hover:border-gray-600"
                : "bg-white border-gray-200 hover:border-blue-300"
            } ${
              patient.tat_breached && !patient.is_done
                ? "border-l-4 border-red-400 bg-red-50 dark:bg-red-900/20"
                : patient.is_done
                ? "border-l-4 border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                : patient.awaitingHistory
                ? "border-l-4 border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                : ""
            }`}
          >
            {/* Card Header */}
            <div
              className={`p-4 border-b ${
                darkMode ? "border-gray-700" : "border-gray-100"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <input
                  type="checkbox"
                  checked={selectedPatients.includes(patient.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handlePatientSelect(patient.id);
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                    patient.is_done
                      ? "bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200"
                      : patient.awaitingHistory
                      ? "bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200"
                      : "bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200"
                  }`}
                >
                  {patient.is_done
                    ? "✅ Completed"
                    : patient.awaitingHistory
                    ? "📋 Awaiting History"
                    : "⏳ Pending"}
                </span>
              </div>

              <h3
                className={`text-lg font-bold mb-1 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {patient.patient_name}
              </h3>
              <p
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                ID: {patient.patient_id}
              </p>

              <div className="flex flex-wrap gap-1 mt-2">
                {patient.urgent && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-bold bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-full">
                    🚨 URGENT
                  </span>
                )}
                {patient.vip && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-bold bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-full">
                    👑 VIP
                  </span>
                )}
                {patient.tat_breached && !patient.is_done && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-bold bg-red-200 dark:bg-red-700 text-red-900 dark:text-red-100 rounded-full animate-pulse">
                    ⚠️ OVERDUE
                  </span>
                )}
                {patient.radiologist && patient.radiologist.length > 0 ? (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 rounded-full">
                    🩺{" "}
                    {patient.radiologist.map((r, i) => (
                      <span key={i}>
                        Dr. {r}
                        {i !== patient.radiologist.length - 1 && ", "}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 rounded-full">
                    ⚕️ Unassigned
                  </span>
                )}
              </div>
            </div>

            {/* Card Body */}
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Age:
                  </span>
                  <span
                    className={`ml-1 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {patient.age}
                  </span>
                </div>
                <div>
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Gender:
                  </span>
                  <span
                    className={`ml-1 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {patient.gender}
                  </span>
                </div>
                <div>
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Study Date:
                  </span>
                  <span
                    className={`ml-1 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {patient.study_date}
                  </span>
                </div>
                <div>
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Modality:
                  </span>
                  <span className="ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                    {patient.modality}
                  </span>
                </div>
                <div className="text-sm">
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Body Part:
                  </span>
                  <span
                    className={`ml-1 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {patient.body_part_examined}
                  </span>
                </div>
                <div className="text-sm">
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Study:
                  </span>
                  <p
                    className={`truncate ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                    title={patient.study_description}
                  >
                    {patient.study_description}
                  </p>
                </div>
                <div className="text-sm">
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    TAT Status:
                  </span>
                  <span
                    className={`text-xs font-bold ${getTimeRemainingColor(
                      getLiveTimeRemaining(
                        patient.id,
                        patient.awaitingHistory
                          ? patient.elapsed_seconds
                          : patient.time_remaining
                      ),
                      patient.tat_breached,
                      patient.is_done,
                      patient.awaitingHistory
                    )}`}
                  >
                    {formatTimeRemaining(
                      getLiveTimeRemaining(
                        patient.id,
                        patient.awaitingHistory
                          ? patient.elapsed_seconds
                          : patient.time_remaining
                      ),
                      patient.is_done,
                      patient.overdue_seconds,
                      patient.awaitingHistory
                    )}
                  </span>
                </div>
              </div>

              {patient.notes && (
                <div className="text-sm">
                  <span
                    className={`font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Clinical History:
                  </span>
                  <p
                    className={`text-xs mt-1 line-clamp-2 ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {patient.notes}
                  </p>
                </div>
              )}

              {patient.history_files && patient.history_files.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {patient.history_files.map((file, index) => {
                    return (
                      <button
                        key={index}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const urlResult =
                              await CoordinatorHandler.getPresignedUrl(file);
                            if (urlResult.success && urlResult.url) {
                              window.open(urlResult.url, "_blank");
                            } else {
                              alert("Failed to open history file");
                            }
                          } catch (error) {
                            console.error("Error opening history file:", error);
                            alert("Failed to open history file");
                          }
                        }}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-200 dark:hover:bg-indigo-700 transition-all duration-200"
                      >
                        H{index + 1}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Card Footer */}
            <div
              className={`px-4 py-3 border-t flex justify-between items-center ${
                darkMode
                  ? "bg-gray-900 border-gray-700"
                  : "bg-gray-50 border-gray-100"
              }`}
            >
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedReport(patient);
                  }}
                  className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-all duration-200"
                >
                  ✏️ Edit
                </button>
                {patient.patient_reports &&
                  patient.patient_reports.length > 0 && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const urlResult =
                            await CoordinatorHandler.getPresignedUrl(
                              patient.patient_reports[0].file_key
                            );
                          if (urlResult.success && urlResult.url) {
                            window.open(urlResult.url, "_blank");
                          } else {
                            alert("Failed to open report");
                          }
                        } catch (error) {
                          console.error("Error opening report:", error);
                          alert("Failed to open report");
                        }
                      }}
                      className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-700 transition-all duration-200"
                    >
                      📄 Report
                    </button>
                  )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPatients.length === 0 && (
        <div className="text-center py-16">
          <div
            className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
              darkMode
                ? "bg-gray-800"
                : "bg-gradient-to-r from-gray-100 to-gray-200"
            }`}
          >
            <span className="text-4xl text-gray-400">📋</span>
          </div>
          <h3
            className={`text-lg font-semibold mb-2 ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            No patients found
          </h3>
          <p className={darkMode ? "text-gray-400" : "text-gray-500"}>
            Try adjusting your search criteria or filters.
          </p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div
        className={`flex justify-center items-center min-h-screen transition-colors duration-200 ${
          darkMode
            ? "bg-gray-900"
            : "bg-gradient-to-br from-blue-50 to-indigo-100"
        }`}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p
            className={`text-lg font-medium ${
              darkMode ? "text-white" : "text-gray-600"
            }`}
          >
            Loading Dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-200 ${
        darkMode
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
          : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"
      }`}
    >
      <CoordinatorHeader
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        currentCoordinator={currentCoordinator}
        firstName={firstName}
        lastName={lastName}
        stats={stats}
        headerRef={headerRef}
      />

      <div className="w-full px-2 sm:px-3 lg:px-4 py-2">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 w-full">
          <div
            className={`p-4 rounded-xl shadow-md border transition-all duration-300 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3
                  className={`text-xxs font-semibold uppercase tracking-wide ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Total Cases
                </h3>
                <p
                  className={`text-xl font-bold mt-1 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stats.totalCases}
                </p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">📁</span>
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <div
                className={`w-full rounded-full h-2 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              >
                <div
                  className="bg-gradient-to-r from-blue-400 to-blue-500 h-2 rounded-full"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>
          </div>

          <div
            className={`p-4 rounded-xl shadow-md border transition-all duration-300 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3
                  className={`text-xxs font-semibold uppercase tracking-wide ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Pending Cases
                </h3>
                <p
                  className={`text-xl font-bold mt-1 ${
                    darkMode ? "text-amber-400" : "text-amber-600"
                  }`}
                >
                  {stats.pendingCases}
                </p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-amber-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">⏳</span>
              </div>
            </div>
          </div>

          <div
            className={`p-4 rounded-xl shadow-md border transition-all duration-300 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3
                  className={`text-xxs font-semibold uppercase tracking-wide ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Reported Cases
                </h3>
                <p
                  className={`text-xl font-bold mt-1 ${
                    darkMode ? "text-emerald-400" : "text-emerald-600"
                  }`}
                >
                  {stats.reportedCases}
                </p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">✅</span>
              </div>
            </div>
          </div>

          <div
            className={`p-4 rounded-xl shadow-md border-l-4 border-red-400 transition-all duration-300 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3
                  className={`text-xxs font-semibold uppercase tracking-wide ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Overdue Cases
                </h3>
                <p
                  className={`text-xl font-bold mt-1 ${
                    darkMode ? "text-red-400" : "text-red-600"
                  }`}
                >
                  {stats.overdueCases}
                </p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-r from-red-400 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg">🚨</span>
              </div>
            </div>
            {stats.overdueCases > 0 && (
              <div
                className={`mt-2 text-xs font-medium ${
                  darkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                ⚠️ Needs attention
              </div>
            )}
          </div>
        </div>
        {/* Assign/Replace & Bulk Notes Section */}
        {selectedPatients.length > 0 && (
          <div
            className={`flex flex-col p-4 rounded-2xl shadow-lg border mb-8 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            {/* Assign/Replace Section */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">

              {/* Assign / Replace Toggle */}
              <div className={`flex rounded-xl p-1 ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                <button
                  onClick={() => setAssignMode("assign")}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    assignMode === "assign"
                      ? "bg-blue-500 text-white shadow-md scale-105"
                      : darkMode
                      ? "text-gray-300"
                      : "text-gray-600"
                  }`}
                >
                  Assign
                </button>
            
                <button
                  onClick={() => setAssignMode("replace")}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    assignMode === "replace"
                      ? "bg-orange-500 text-white shadow-md scale-105"
                      : darkMode
                      ? "text-gray-300"
                      : "text-gray-600"
                  }`}
                >
                  Replace
                </button>
              </div>
            
              {/* Radiologist Dropdown */}
              <select
                value={selectedRadiologist}
                onChange={(e) => setSelectedRadiologist(e.target.value)}
                className={`px-4 py-3 border rounded-xl min-w-64 ${
                  darkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300"
                }`}
              >
                <option value="">Select Radiologist</option>
                {radiologists.map((rad) => (
                  <option key={rad.id} value={rad.id}>
                    {rad.first_name} {rad.last_name}
                  </option>
                ))}
              </select>
            
              {/* Corporate Dropdown (hidden for corporate users) */}
              {!isCorporate && (
                <select
                  value={selectedCorporate}
                  onChange={(e) => setSelectedCorporate(e.target.value)}
                  className={`px-4 py-3 border rounded-xl min-w-64 ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <option value="">Select Corporate Coordinator</option>
                  {corporateCoordinators.map((corp) => (
                    <option key={corp.id} value={corp.id}>
                      {corp.name} ({corp.email})
                    </option>
                  ))}
                </select>
              )}
            
              {/* SINGLE ACTION BUTTON */}
              <button
                onClick={async () => {
                  if (selectedRadiologist) {
                    await handleAssignRadiologist();
                  }
              
                  if (!isCorporate && selectedCorporate) {
                    await handleAssignCorporate();
                  }
                }}
                disabled={
                  selectedPatients.length === 0 ||
                  (!selectedRadiologist && !selectedCorporate)
                }
                className={`px-8 py-3 rounded-xl font-semibold transition-all shadow-lg ${
                  selectedPatients.length === 0 ||
                  (!selectedRadiologist && !selectedCorporate)
                    ? "bg-gray-300 text-gray-500"
                    : assignMode === "assign"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                }`}
              >
                {assignMode === "assign" ? "Assign" : "Replace"} Selected
                {selectedRadiologist && " Radiologist"}
                {selectedCorporate && " Corporate"}
                 (
                {selectedPatients.length})
              </button>

            
            </div>
            {isCorporate && (
              <button
                onClick={handleSendToU4rad}
                disabled={selectedPatients.length === 0}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed shadow-lg ${
                  selectedPatients.length === 0
                    ? "bg-gray-300 text-gray-500"
                    : "bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700"
                }`}
              >
                Send to U4RAD ({selectedPatients.length})
              </button>
            )}
            
            {/* Bulk Notes Update Section */}
            <div className="flex items-start gap-4 pt-4 border-t border-gray-300 dark:border-gray-600">
              <div className="flex-1">
                <label
                  className={`block text-sm font-semibold mb-2 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Bulk Update Notes ({selectedPatients.length} patients
                  selected)
                </label>
                <textarea
                  value={bulkNoteText}
                  onChange={(e) => setBulkNoteText(e.target.value)}
                  placeholder="This will replace existing notes for all selected patients..."
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 placeholder-gray-500"
                  }`}
                  rows="3"
                />
              </div>

              <div className="flex flex-col gap-2 pt-6">
                <button
                  onClick={() => setBulkNoteText("")}
                  disabled={!bulkNoteText}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                    !bulkNoteText
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : darkMode
                      ? "bg-gray-600 text-gray-300 hover:bg-gray-500 hover:text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Clear Notes
                </button>

                <button
                  onClick={handleBulkUpdateNotes}
                  disabled={
                    isUpdatingBulkNotes ||
                    selectedPatients.length === 0 ||
                    !bulkNoteText
                  }
                  className={`px-6 py-2 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed shadow-lg ${
                    isUpdatingBulkNotes ||
                    selectedPatients.length === 0 ||
                    !bulkNoteText
                      ? "bg-gray-300 text-gray-500"
                      : "bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-green-200"
                  }`}
                >
                  {isUpdatingBulkNotes ? "Updating..." : "Update Notes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <CoordinatorFilters
          darkMode={darkMode}
          filters={filters}
          handleFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          bodyParts={bodyParts}
          getUniqueOptions={getUniqueOptions}
          viewMode={viewMode}
          setViewMode={setViewMode}
          radiologists={radiologists}
          patients={patients}
          filterRef={filterRef}
        />

        {/* Grid Table */}
        <CoordinatorGridTable
          darkMode={darkMode}
          filteredPatients={filteredPatients}
          selectedPatients={selectedPatients}
          handlePatientSelect={handlePatientSelect}
          setSelectedReport={setSelectedReport}
          getLiveTimeRemaining={getLiveTimeRemaining}
          formatTimeRemaining={formatTimeRemaining}
          getTimeRemainingColor={getTimeRemainingColor}
          viewMode={viewMode}
          renderGridView={renderGridView}
          headerHeight={headerHeight}
          filterHeight={filterHeight}
          filters={filters}
          handleFilterChange={handleFilterChange}
          loadMorePatients={loadMorePatients}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          searchLoading={searchLoading}
        />
      </div>

      {/* Edit Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className={`w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-white">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-xl">✏️</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Edit Patient Data</h2>
                    <p className="text-blue-100 text-sm">
                      Update DICOM information and clinical details
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-colors duration-200"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const fd = new FormData(e.target);
                  const newData = {};
                  // fd.forEach((value, key) => {
                  //   if (key !== 'history_file' && key !== 'imaging_views') {
                  //     // Convert string boolean values to actual booleans
                  //     if (key === 'contrast_used' || key === 'is_follow_up') {
                  //       newData[key] = value === 'true';
                  //     } else {
                  //       newData[key] = value;
                  //     }
                  //   }
                  // });
                  fd.forEach((value, key) => {
                    if (key !== "history_file") {
                      newData[key] = value;
                    }
                  });

                  // Handle imaging_views checkboxes as an array
                  const imagingViews = fd.getAll("imaging_views");
                  if (imagingViews.length > 0) {
                    newData["imaging_views"] = imagingViews;
                  }

                  // Extract history files
                  const historyFiles = Array.from(
                    fd.getAll("history_file")
                  ).filter((f) => f && f.size > 0);

                  handleSaveReport(
                    newData,
                    historyFiles.length ? historyFiles : null
                  );
                }}
                className="space-y-6"
              >
                {/* Basic Information */}
                <div
                  className={`p-6 rounded-2xl border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gradient-to-r from-gray-50 to-blue-50 border-gray-100"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-4 flex items-center space-x-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span>👤</span>
                    <span>Basic Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Patient Name
                      </span>
                      <input
                        type="text"
                        name="patient_name"
                        defaultValue={selectedReport.patient_name || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter patient name"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Patient ID
                      </span>
                      <input
                        type="text"
                        name="patient_id"
                        defaultValue={selectedReport.patient_id || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter patient ID"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Age
                      </span>
                      <input
                        type="text"
                        name="age"
                        defaultValue={selectedReport.age || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter age"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Gender
                      </span>
                      <select
                        name="gender"
                        defaultValue={selectedReport.gender || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Study Information */}
                <div
                  className={`p-6 rounded-2xl border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-100"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-4 flex items-center space-x-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span>🔬</span>
                    <span>Study Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Study Date
                      </span>
                      <input
                        type="text"
                        name="study_date"
                        defaultValue={selectedReport.study_date || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter study date"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Study Description
                      </span>
                      <input
                        type="text"
                        name="study_description"
                        defaultValue={selectedReport.study_description || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter study description"
                      />
                    </label>
                    <label className="flex flex-col space-y-2 md:col-span-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Body Part Examined
                      </span>

                      <Select
                        name="body_part_examined"
                        defaultValue={
                          selectedReport.body_part_examined
                            ? {
                                value: selectedReport.body_part_examined,
                                label: selectedReport.body_part_examined,
                              }
                            : null
                        }
                        options={bodyPartOptions}
                        placeholder="Search or select body part..."
                        isSearchable
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </label>
                  </div>
                </div>

                {/* Clinical Information */}
                <div
                  className={`p-6 rounded-2xl border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-100"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-4 flex items-center space-x-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span>📋</span>
                    <span>Clinical Information</span>
                  </h3>
                  <div className="space-y-4">
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Clinical Notes
                      </span>
                      <textarea
                        name="notes"
                        defaultValue={selectedReport.notes || ""}
                        rows={4}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 resize-none ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter clinical notes and history"
                      />
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex flex-col space-y-2">
                        <span
                          className={`text-sm font-semibold ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Referring Doctor
                        </span>
                        <input
                          type="text"
                          name="referring_doctor_name"
                          defaultValue={
                            selectedReport.referring_doctor_name || ""
                          }
                          className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 ${
                            darkMode
                              ? "bg-gray-600 border-gray-500 text-white"
                              : "border-gray-300"
                          }`}
                          placeholder="Enter doctor name"
                        />
                      </label>
                      <label className="flex flex-col space-y-2">
                        <span
                          className={`text-sm font-semibold ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Case Type
                        </span>
                        <div className="flex space-x-4 pt-2">
                          {["VIP", "URGENT", "MLC"].map((option) => (
                            <label
                              key={option}
                              className="inline-flex items-center space-x-2"
                            >
                              <input
                                type="checkbox"
                                name="case_type"
                                value={option}
                                defaultChecked={
                                  Array.isArray(selectedReport.case_type)
                                    ? selectedReport.case_type.includes(option)
                                    : selectedReport.case_type === option
                                }
                                className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500 rounded"
                              />
                              <span
                                className={`text-sm font-medium ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                {option}
                              </span>
                            </label>
                          ))}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div
                  className={`p-6 rounded-2xl border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-100"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-4 flex items-center space-x-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span>📞</span>
                    <span>Contact Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Email Address
                      </span>
                      <input
                        type="email"
                        name="email"
                        defaultValue={selectedReport.email || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter email address"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        WhatsApp Number
                      </span>
                      <input
                        type="tel"
                        name="whatsapp_number"
                        defaultValue={selectedReport.whatsapp_number || ""}
                        className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter WhatsApp number"
                      />
                    </label>
                  </div>
                </div>

                {/* File Upload Section */}
                <div
                  className={`p-6 rounded-2xl border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-100"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-4 flex items-center space-x-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span>📎</span>
                    <span>Patient History Files</span>
                  </h3>

                  {/* Show existing history file(s) if any */}
                  {selectedReport.history_files &&
                  selectedReport.history_files.length > 0 ? (
                    <div
                      className={`mb-4 p-4 rounded-xl border ${
                        darkMode
                          ? "bg-gray-600 border-indigo-700"
                          : "bg-white border-indigo-200"
                      }`}
                    >
                      <h4
                        className={`text-sm font-semibold mb-3 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Current Files:
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {selectedReport.history_files.map((file, index) => {
                          return (
                            <button
                              key={index}
                              onClick={async (e) => {
                                e.preventDefault();
                                const urlResult =
                                  await CoordinatorHandler.getPresignedUrl(
                                    file
                                  );
                                if (urlResult.success) {
                                  window.open(urlResult.url, "_blank");
                                }
                              }}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-800 dark:text-blue-200 hover:from-blue-200 hover:to-indigo-200 dark:hover:from-blue-800 dark:hover:to-indigo-800 border border-blue-200 dark:border-blue-600 transition-all duration-200"
                            >
                              📄 History File {index + 1}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`mb-4 p-4 rounded-xl border ${
                        darkMode
                          ? "bg-gray-600 border-gray-500"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <p
                        className={`text-sm text-center py-2 ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No history files uploaded yet
                      </p>
                    </div>
                  )}

                  <label className="flex flex-col space-y-2">
                    <span
                      className={`text-sm font-semibold ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Upload Additional Files
                    </span>
                    <input
                      type="file"
                      name="history_file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium hover:file:bg-indigo-100 ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white file:bg-indigo-900 file:text-indigo-200"
                          : "border-gray-300 file:bg-indigo-50 file:text-indigo-700"
                      }`}
                    />
                    <p
                      className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB
                      per file)
                    </p>
                  </label>
                </div>

                {/* ADD THIS NEW SECTION HERE */}
                <div
                  className={`p-6 rounded-2xl border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gradient-to-r from-cyan-50 to-teal-50 border-cyan-100"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-4 flex items-center space-x-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <span>✅</span>
                    <span>Pre-Scan Verification Checklist</span>
                  </h3>

                  <div className="space-y-4 text-sm">
                    <div>
                      <p
                        className={`font-semibold mb-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        1. Body Part Confirmation
                      </p>
                      <p
                        className={`ml-4 ${
                          darkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        ➝ Have you filled the body part details?
                      </p>
                    </div>

                    <div>
                      <p
                        className={`font-semibold mb-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        2. Contrast Imaging Verification
                      </p>
                      <div className="ml-4 flex space-x-4">
                        <label className="inline-flex items-center space-x-2">
                          <input
                            type="radio"
                            name="contrast_used"
                            value="true"
                            defaultChecked={selectedReport.contrastUsed}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span
                            className={
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }
                          >
                            Yes
                          </span>
                        </label>
                        <label className="inline-flex items-center space-x-2">
                          <input
                            type="radio"
                            name="contrast_used"
                            value="False"
                            defaultChecked={!selectedReport.contrastUsed}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span
                            className={
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }
                          >
                            No
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <p
                        className={`font-semibold mb-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        3. Comparative / Follow-Up Verification
                      </p>
                      <div className="ml-4 flex space-x-4">
                        <label className="inline-flex items-center space-x-2">
                          <input
                            type="radio"
                            name="is_follow_up"
                            value="true"
                            defaultChecked={selectedReport.isFollowUp}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span
                            className={
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }
                          >
                            Yes
                          </span>
                        </label>
                        <label className="inline-flex items-center space-x-2">
                          <input
                            type="radio"
                            name="is_follow_up"
                            value="False"
                            defaultChecked={!selectedReport.isFollowUp}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span
                            className={
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }
                          >
                            No
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <p
                        className={`font-semibold mb-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        4. Imaging View Verification (for X-ray)
                      </p>
                      <div className="ml-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {["AP", "PA", "Lateral", "Oblique"].map((view) => (
                          <label
                            key={view}
                            className="inline-flex items-center space-x-2"
                          >
                            <input
                              type="checkbox"
                              name="imaging_views"
                              value={view}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                            />
                            <span
                              className={`text-sm ${
                                darkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {view}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => setSelectedReport(null)}
                    className="px-8 py-3 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-xl font-semibold transition-all duration-200 hover:from-gray-500 hover:to-gray-600 transform hover:scale-105 shadow-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 shadow-lg"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Coordinator;